<template>
  <Modal
    size="md"
    anchor="top"
    ref="confirmNotifyImmediate"
    :onClose="onModalClose"
    :width="modalWidth">
    <template #title>
      <h2 class="p-2 text-center mx-auto">Send Notification Immediately</h2>
    </template>
    <template #content>
      <div class="flex flex-col">
        <div
          v-if="serverCommunication.waitOnServerResponseDifference">
          <div class="flex flex-col items-center opacity-75 top-1/2 my-auto mx-auto block relative" role="status">
            <div class="flex-none spinner-border animate-spin inline-block w-16 h-16 border-4 rounded-full" role="status"></div>
            <span class="flex top-1/2 pt-2 my-0 mx-auto block text-center font-italic">
              Acquiring Route Notification Difference
            </span>
          </div>
        </div>
        <div class="flex p-4 w-full justify-center"
          v-if="serverCommunication.displayErrorDifference">
          <span class="text-red-400">
            Error acquiring difference
          </span>
        </div>
        <div
          v-if="serverCommunication.waitOnServerResponseNotify">
          <div class="flex flex-col items-center opacity-75 top-1/2 my-auto mx-auto block relative" role="status">
            <div class="flex-none spinner-border animate-spin inline-block w-16 h-16 border-4 rounded-full" role="status"></div>
            <span class="flex top-1/2 pt-2 my-0 mx-auto block text-center font-italic">
              Sending Notification
            </span>
          </div>
        </div>
        <div class="flex p-4 w-full justify-center"
          v-if="serverCommunication.displayErrorNotify">
          <span class="text-red-400">
            Error sending notification for request
          </span>
        </div>
        <div class="flex p-4 w-full justify-center"
          v-if="serverCommunication.displaySuccessNotify">
          <div class="flex flex-row border-green-200 border-b-2">
            <svg
              width="24px"
              height="24px"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg">
              <polygon
                fill-rule="evenodd"
                points="9.707 14.293 19 5 20.414 6.414 9.707 17.121 4 11.414 5.414 10"
              />
            </svg>
            <span class="text-green-600">
              Notification Success
            </span>
          </div>
        </div>
        <div
          v-if="notificationDiff !== undefined">
          <div
            v-if="notificationDiff.no_prev_notification">
            <div class="flex m-4 text-gray-500 justify-center">
              No Previous Route Notification
            </div>
          </div>
          <div
            v-else>
            <div class="flex m-4 text-gray-500 justify-center"
              v-if="notificationDiffComputed.empty">
              No Difference between current data and last notification
            </div>
            <div class="flex flex-col gap-2"
              v-else>
              <NotifyDiffDisplayRow
                v-if="notificationDiffComputed.effectiveDate !== undefined"
                :sectionName="'Effective Date'">
                <template #prev>
                  <span class="text-sm">{{ notificationDiffComputed.effectiveDate.prev }}</span>
                </template>
                <template #curr>
                  <span class="text-sm">{{ notificationDiffComputed.effectiveDate.curr }}</span>
                </template>
              </NotifyDiffDisplayRow>
              <NotifyDiffDisplayRow
                v-if="notificationDiffComputed.accommodations !== undefined"
                :sectionName="'Accommodations'">
                <template #prev>
                  <ul class="list-dic">
                    <li
                      v-for="accommodationString in notificationDiffComputed.accommodations.prev"
                      :key="accommodationString">
                      <span class="text-sm">{{ accommodationString }}</span>
                    </li>
                  </ul>
                </template>
                <template #curr>
                  <ul class="list-dic">
                    <li
                      v-for="accommodationString in notificationDiffComputed.accommodations.curr"
                      :key="accommodationString">
                      <span class="text-sm">{{ accommodationString }}</span>
                    </li>
                  </ul>
                </template>
              </NotifyDiffDisplayRow>
              <NotifyDiffDisplayRow
                v-if="notificationDiffComputed.emailList !== undefined"
                :sectionName="'Email List'">
                <template #prev>
                  <ul class="list-dic">
                    <li class="truncate"
                      v-for="emailString in notificationDiffComputed.emailList.prev"
                      :key="emailString">
                      <span class="text-sm">{{ emailString }}</span>
                    </li>
                  </ul>
                </template>
                <template #curr>
                  <ul class="list-dic">
                    <li class="truncate"
                      v-for="emailString in notificationDiffComputed.emailList.curr"
                      :key="emailString">
                      <span class="text-sm">{{ emailString }}</span>
                    </li>
                  </ul>
                </template>
              </NotifyDiffDisplayRow>
              <NotifyDiffDisplayRow
                v-if="notificationDiffComputed.days !== undefined"
                :sectionName="'Days'">
                <template #prev>
                  <ul class="list-dic">
                    <li
                      v-for="dayString in notificationDiffComputed.days.prev"
                      :key="dayString">
                      <span class="text-sm">{{ dayString }}</span>
                    </li>
                  </ul>
                </template>
                <template #curr>
                  <ul class="list-dic">
                    <li
                      v-for="dayString in notificationDiffComputed.days.curr"
                      :key="dayString">
                      <span class="text-sm">{{ dayString }}</span>
                    </li>
                  </ul>
                </template>
              </NotifyDiffDisplayRow>
              <div class="bg-gray-100"
                v-if="notificationDiffComputed.daysDetail !== undefined">
                <div class="p-4">
                  <div class="border-b-2">
                    Days Detailed
                  </div>
                  <NotifyDiffDisplayRow
                    v-for="weekdayRemapped in notificationDiffComputed.daysDetail"
                    :key="weekdayRemapped.weekday"
                    :sectionName="weekdayRemapped.weekday">
                    <template #prev>
                      <div class="flex flex-col gap-2">
                        <div
                          v-for="fieldDiff in weekdayRemapped.prev"
                          :key="fieldDiff.name">
                          <div class="grid grid-cols-3 gap-2">
                            <div class="col-span-1 text-sm font-bold text-gray-500">
                              {{ fieldDiff.name }}
                            </div>
                            <div class="col-span-2 text-sm">
                              {{ fieldDiff.value }}
                            </div>
                          </div>
                        </div>
                      </div>
                    </template>
                    <template #curr>
                      <div class="flex flex-col gap-2">
                        <div
                          v-for="fieldDiff in weekdayRemapped.curr"
                          :key="fieldDiff.name">
                          <div class="grid grid-cols-3 gap-2">
                            <div class="col-span-1 text-sm font-bold text-gray-500">
                              {{ fieldDiff.name }}
                            </div>
                            <div class="col-span-2 text-sm">
                              {{ fieldDiff.value }}
                            </div>
                          </div>
                        </div>
                      </div>
                    </template>
                  </NotifyDiffDisplayRow>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>
    <template #footer>
      <Button
        type="button"
        color="gray"
        :disabled="serverCommunication.waitOnServerResponseNotify"
        class="rounded-md font-medium px-3 py-2 transition-colors shadow-sm disabled:bg-gray-100 disabled:text-gray-400"
        @click="onNotifyImmediateConfirmClick(false)">
        Notify On Change
      </Button>
      <Button
        type="button"
        color="blue"
        :disabled="serverCommunication.waitOnServerResponseNotify"
        class="rounded-md font-medium px-3 py-2 transition-colors shadow-sm disabled:bg-gray-100 disabled:text-gray-400"
        @click="onNotifyImmediateConfirmClick(true)">
        Notify Anyways
      </Button>
    </template>
  </Modal>
</template>

<script>
import Modal from '../Modal.vue'
// import Input from '../Input.vue'
import Button from '../Button.vue'

import NotifyDiffDisplayRow from './NotifyDiffDisplayRow.vue'

import DROPDOWN_LISTS from '../DropdownLists.json'
import { getRouteNotificationInfoDiff, postRouteNotificationInfo } from '../../services/api.js'

import { forEach } from 'lodash'

export default {
  components: {
    Modal,
    // Input,
    Button,
    NotifyDiffDisplayRow,
  },
  props: {
    show: Boolean,
    dbId: Number,
  },
  data() {
    return {
      notificationDiff: undefined,

      statusSelected: 'routing',
      previousStatusSelected: '',
      componentSelected: '',
      serverCommunication: {
        waitOnServerResponseNotify: false,
        waitOnServerResponseDifference: false,
        displayErrorNotify: false,
        displaySuccessNotify: false,
        displayErrorDifference: false,
      },
      inputModels: {
        additionalInfo: {
          sendMessage: '',
          email: '',
        },
      },
      notifyStatusModel: {
        errors: {
          errorGettingData: false,
          errorSendingNotification: false,
        },
        lastNotification: undefined,
        openLastNotification: false,
        markedForNotification: false,
        forceNotify: false,
      },
    }
  },
  methods: {
    onModalClose() {
      this.$emit('update:show', false)
    },
    async getRouteNotificationInfoDiff() {
      this.serverCommunication.waitOnServerResponseDifference = true
      this.serverCommunication.displayErrorDifference = false

      try {
        this.notificationDiff = undefined
        let result = await getRouteNotificationInfoDiff(this.dbId)

        if (result.status == 206) {
          if (!result.data.complete) {
            setTimeout(this.getRouteNotificationInfoDiff, 15 * 1000)
          }
          else {
            this.serverCommunication.waitOnServerResponseNotify = false
            this.serverCommunication.displayErrorNotify = true
          }
        }
        else if (result.status == 200) {
          this.notificationDiff = result.data
          this.serverCommunication.waitOnServerResponseDifference = false
        }
        else {
          this.serverCommunication.waitOnServerResponseDifference = false
          this.serverCommunication.displayErrorDifference = true
        }
      }
      catch {
        this.serverCommunication.displayErrorDifference = true
        this.serverCommunication.waitOnServerResponseDifference = false
      }
    },
    async onModalOpen() {
      this.serverCommunication.waitOnServerResponseDifference = true
      this.serverCommunication.displayErrorDifference = false

      this.serverCommunication.waitOnServerResponseNotify = false
      this.serverCommunication.displayErrorNotify = false
      this.serverCommunication.displaySuccessNotify = false

      this.getRouteNotificationInfoDiff()
    },
    async onNotifyImmediateConfirmClick(forceNotify) {
      this.serverCommunication.waitOnServerResponseNotify = true
      this.serverCommunication.displayErrorNotify = false
      this.serverCommunication.displaySuccessNotify = false
      try {
        let result = await postRouteNotificationInfo({
            action: 'notify_immediate',
            options: { 'force_notify': forceNotify, },
          }, this.dbId)

        if (result.status == 206) {
          if (!result.data.complete) {
            setTimeout(this.onNotifyImmediateConfirmClick, 15 * 1000, forceNotify)
          }
          else {
            this.serverCommunication.waitOnServerResponseNotify = false
            this.serverCommunication.displayErrorNotify = true
          }
        }
        else if (result.status == 200) {
          this.serverCommunication.displaySuccessNotify = true
          this.serverCommunication.waitOnServerResponseNotify = false
          this.$emit('notifiedImmediate')
        }
        else {
          this.serverCommunication.waitOnServerResponseNotify = false
          this.serverCommunication.displayErrorNotify = true
        }
        // slotProps.closeModal(true)
      }
      catch (error) {
        this.serverCommunication.displayErrorNotify = true
        this.serverCommunication.waitOnServerResponseNotify = false
        throw error
      }
    },
  },
  computed: {
    modalWidth() {
      if (this.notificationDiff == undefined) {
        return "2"
      }
      else if (this.notificationDiffComputed.daysDetail == undefined) {
        return "2"
      }
      else {
        return "4"
      }
    },
    notificationDiffComputed() {
      if (this.notificationDiff == undefined) {
        return {
          empty: true,
        }
      }

      // effective_date
      // accommodations
      // email_list
      // days
      // days_detail
      // no_prev_notification

      let diffFields = {
        empty: true
      }

      if (this.notificationDiff['effective_date']) {
        diffFields['effectiveDate'] = {
          prev: new Date(this.notificationDiff['effective_date'].prev).toLocaleString('en-US', {
            year: 'numeric',
            month: 'numeric',
            day: 'numeric',
            timeZone: 'UTC',
          }),
          curr: new Date(this.notificationDiff['effective_date'].curr).toLocaleString('en-US', {
            year: 'numeric',
            month: 'numeric',
            day: 'numeric',
            timeZone: 'UTC',
          })
        }
        diffFields.empty = false
      }
      if (this.notificationDiff['accommodations']) {
        diffFields['accommodations'] = {
          prev: [],
          curr: []
        }
        forEach(this.notificationDiff['accommodations'], (value, accommodation) => {
          if (value.prev) {
            diffFields['accommodations'].prev.push(accommodation)
          }
          if (value.curr) {
            diffFields['accommodations'].curr.push(accommodation)
          }
        })
        diffFields.empty = false
      }
      if (this.notificationDiff['email_list']) {
        diffFields['emailList'] = this.notificationDiff['email_list']
        diffFields.empty = false
      }

      if (this.notificationDiff['days']) {
        diffFields['days'] = {
          prev: this.notificationDiff['days'].prev.map(dayString => DROPDOWN_LISTS.weekdays[dayString]),
          curr: this.notificationDiff['days'].curr.map(dayString => DROPDOWN_LISTS.weekdays[dayString]),
        }
        diffFields.empty = false
      }

      if (this.notificationDiff['days_detail']) {
        const ORDER = {
          'sun': 0,
          'mon': 1,
          'tue': 2,
          'wed': 3,
          'thu': 4,
          'fri': 5,
          'sat': 6,
        }
        const FIELD_NAME_MAP = {
          'pickup_vehicle_code': 'Pickup Vehicle',
          'dropoff_vehicle_code': 'Dropoff Vehicle',
          'pickup_building_code': 'Pickup Building Code',
          'dropoff_building_code': 'Dropoff Building Code',
          'pickup_time': 'Pickup Time',
          'dropoff_time': 'Dropoff Time',
          'pickup_bus_stop_description': 'Pickup Bus Stop',
          'dropoff_bus_stop_description': 'Dropoff Bus Stop',
        }
        diffFields['daysDetail'] = []

        forEach(this.notificationDiff['days_detail'], (fields, weekday) => {
          let weekdayRemapped = {
            order: ORDER[weekday],
            weekday: weekday,
            prev: [],
            curr: [],
          }
          forEach(fields, (value, fieldName) => {
            let fieldInfoPrev = {
              name: FIELD_NAME_MAP[fieldName],
              value: value.prev,
            }
            let fieldInfoCurr = {
              name: FIELD_NAME_MAP[fieldName],
              value: value.curr,
            }
            weekdayRemapped.prev.push(fieldInfoPrev)
            weekdayRemapped.curr.push(fieldInfoCurr)
          })
          diffFields['daysDetail'].push(weekdayRemapped)
        })
        diffFields['daysDetail'].sort(weekdayRemapped => -weekdayRemapped.order)
        diffFields.empty = false
      }

      console.log(diffFields)

      return diffFields
    },
  },
  watch: {
    show(newValue) {
      if (newValue) {
        this.$refs.confirmNotifyImmediate.openModal()
        this.onModalOpen()
      }
    },
  },
  // mounted() {
  // },
  // beforeDestroy() {
  // },
  // created() {
  // }
}
</script>
